import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46ba8d0f-50d1-45b0-932d-164e40ecbfdf/artifacts/l81b46nl_K2B_Logo_Final-2.png";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <Card className="w-full max-w-md border-border bg-card" data-testid="forgot-password-card">
                <CardHeader className="space-y-4 text-center pb-2">
                    <div className="flex justify-center mb-2">
                        <img src={LOGO_URL} alt="K2B Learning" className="h-14" />
                    </div>
                    <h2 className="text-2xl font-semibold text-foreground" style={{ fontFamily: "Outfit" }}>
                        {sent ? "Check Your Email" : "Reset Password"}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {sent
                            ? "If an account with that email exists, a password reset link has been sent to your inbox."
                            : "Enter your email address and we'll send you a reset link."
                        }
                    </p>
                </CardHeader>
                <CardContent>
                    {sent ? (
                        <div className="text-center space-y-6">
                            <div className="flex justify-center">
                                <div className="p-4 rounded-full" style={{ backgroundColor: "#5BA88E20" }}>
                                    <CheckCircle2 className="h-10 w-10" style={{ color: "#5BA88E" }} />
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Check your spam folder if you don't see the email. It may take a few minutes to arrive.
                            </p>
                            <div className="space-y-3">
                                <Button
                                    data-testid="send-another-btn"
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => { setSent(false); setEmail(""); }}
                                >
                                    Send to a different email
                                </Button>
                                <Link to="/login" className="block">
                                    <Button data-testid="back-to-login-btn" variant="ghost" className="w-full gap-2 text-muted-foreground">
                                        <ArrowLeft className="h-4 w-4" /> Back to Sign In
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div data-testid="forgot-error" className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-sm text-muted-foreground">Email Address</Label>
                                <Input
                                    data-testid="forgot-email-input"
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    className="bg-background border-border"
                                    required
                                />
                            </div>
                            <Button
                                data-testid="forgot-submit-btn"
                                type="submit"
                                className="w-full gap-2"
                                disabled={loading}
                                style={{ backgroundColor: "#4A7AB5" }}
                            >
                                {loading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                        Sending...
                                    </div>
                                ) : (
                                    <>
                                        <Mail className="h-4 w-4" /> Send Reset Link
                                    </>
                                )}
                            </Button>
                            <Link to="/login" className="block">
                                <Button data-testid="back-to-login-link" variant="ghost" className="w-full gap-2 text-muted-foreground">
                                    <ArrowLeft className="h-4 w-4" /> Back to Sign In
                                </Button>
                            </Link>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
